import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { api } from '@/lib/api'
import { daysAgoInMalaysia } from '@/lib/date'
import type { SalesReport } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import LogoLoader from '@/components/ui/LogoLoader'
import DateRangeBar from '@/components/report/DateRangeBar'
import SalesTab from '@/pages/admin/reports/SalesTab'
import { buildReportWorkbook } from '@/pages/admin/reports/excel'

/**
 * Two readings of the same endpoint over one date range: the company's sales,
 * and the caller's own.
 *
 * Deliberately not a copy of the admin Reports page, which wires six queries,
 * five tabs and a multi-tab workbook that a sales login cannot fetch anyway.
 */
export default function SalesReports() {
  const [from, setFrom] = useState(daysAgoInMalaysia(29))
  const [to, setTo] = useState(daysAgoInMalaysia(0))
  const [mine, setMine] = useState(false)

  const report = useQuery({
    queryKey: ['sales-report', from, to, mine],
    queryFn: async () =>
      (await api.get<{ data: SalesReport }>('/sales/reports/sales', {
        params: { from, to, ...(mine ? { mine: 1 } : {}) },
      })).data.data,
    placeholderData: (previous) => previous,
  })

  const applyPreset = (range: { from: string; to: string }) => {
    setFrom(range.from)
    setTo(range.to)
  }

  const download = () => {
    if (!report.data) return
    buildReportWorkbook('sales', { from, to }, { sales: report.data })
  }

  return (
    <div>
      <PageHeader
        title="Sales"
        subtitle="Revenue and volume over a date range"
        actions={
          <Button variant="outline" onClick={download} disabled={!report.data}>
            <Download className="size-4" /> Excel
          </Button>
        }
      />

      <Card className="mb-5">
        <DateRangeBar from={from} to={to} onFrom={setFrom} onTo={setTo} onPreset={applyPreset} />

        <div className="mt-4 border-t border-line pt-4">
          <label className="flex items-start gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={mine}
              onChange={(e) => setMine(e.target.checked)}
              className="mt-0.5 size-4 accent-primary"
            />
            <span>
              Only my orders
              {/* Most orders are storefront self-service and have no PIC at
                  all, so without this caption the filtered number reads as a
                  collapse rather than a different question. */}
              <span className="block text-xs font-normal text-muted">
                Counts only orders where you are the recorded PIC - the walk-in and WhatsApp orders you took.
                Storefront checkouts have no PIC and are not included.
              </span>
            </span>
          </label>
        </div>
      </Card>

      <SectionLabel className="mb-3">
        {mine ? 'My performance' : 'Company sales'} ({from} to {to})
      </SectionLabel>

      {report.isLoading || !report.data ? <LogoLoader /> : <SalesTab data={report.data} />}
    </div>
  )
}
